import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { useSessionState } from '@/hooks/use-session-state';
import { downloadXlsx, dateStamp } from '@/lib/download-xlsx';
import { ReportExportButton } from './report-export-button';

/** KDV beyanname özeti (trading): Hesaplanan 391.xx vs İndirilecek 191.xx by rate, from the GL. */
export function TradingKdvReport() {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useSessionState('trading:accounting-reports:kdv:startDate', startOfMonth.toISOString().split('T')[0] ?? '');
  const [endDate, setEndDate] = useSessionState('trading:accounting-reports:kdv:endDate', today.toISOString().split('T')[0] ?? '');

  const { data, isLoading, error } = trpc.tradingFinancialReports.getVatReturnSummary.useQuery({
    startDate: new Date(startDate || startOfMonth),
    endDate: new Date(endDate || today),
  });

  const rates = data ? [...new Set([...Object.keys(data.outputKDV.byRate), ...Object.keys(data.inputKDV.byRate)])].sort((a, b) => Number(a) - Number(b)) : [];

  return (
    <div className="space-y-4">
      <div className="bg-card flex flex-wrap items-end gap-4 rounded-lg border p-4">
        <div className="space-y-2">
          <Label>Başlangıç / From</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-48" />
        </div>
        <div className="space-y-2">
          <Label>Bitiş / To</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-48" />
        </div>
      </div>

      {data && (
        <ReportExportButton
          onExport={() =>
            downloadXlsx(
              [
                ...rates.map((rate) => ({
                  'KDV Oranı': `%${rate}`,
                  'Hesaplanan KDV (391)': data.outputKDV.byRate[rate]?.amount ?? 0,
                  'İndirilecek KDV (191)': data.inputKDV.byRate[rate]?.amount ?? 0,
                  Net: (data.outputKDV.byRate[rate]?.amount ?? 0) - (data.inputKDV.byRate[rate]?.amount ?? 0),
                })),
                { 'KDV Oranı': 'TOPLAM', 'Hesaplanan KDV (391)': data.outputKDV.total, 'İndirilecek KDV (191)': data.inputKDV.total, Net: data.netKDV.amount },
              ],
              'KDV',
              `kdv-beyanname-${dateStamp()}.xlsx`
            )
          }
        />
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">{error.message}</div>
      ) : data ? (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
              <p className="text-sm text-green-700 dark:text-green-300">{data.outputKDV.description}</p>
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(data.outputKDV.total)}</p>
            </div>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
              <p className="text-sm text-blue-700 dark:text-blue-300">{data.inputKDV.description}</p>
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(data.inputKDV.total)}</p>
            </div>
            <div className={`rounded-lg border p-4 ${data.netKDV.amount >= 0 ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20' : 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'}`}>
              <p className="text-sm text-muted-foreground">{data.netKDV.description}</p>
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(Math.abs(data.netKDV.amount))}</p>
            </div>
          </div>

          <div className="bg-card overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">KDV oranı / Rate</th>
                  <th className="px-4 py-2 text-right font-medium">Hesaplanan (391)</th>
                  <th className="px-4 py-2 text-right font-medium">İndirilecek (191)</th>
                  <th className="px-4 py-2 text-right font-medium">Net</th>
                </tr>
              </thead>
              <tbody>
                {rates.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-sm text-muted-foreground">
                      Bu dönemde KDV hareketi yok. / No KDV postings in this period.
                    </td>
                  </tr>
                )}
                {rates.map((rate) => {
                  const out = data.outputKDV.byRate[rate]?.amount ?? 0;
                  const inp = data.inputKDV.byRate[rate]?.amount ?? 0;
                  return (
                    <tr key={rate} className="border-b last:border-b-0">
                      <td className="px-4 py-2">%{rate}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(out)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(inp)}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium">{formatCurrency(out - inp)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
